/**
 * One day's propers.
 *
 * ONE PAGE PER DAY, NOT PER DAY AND YEAR. A day whose communion varies by
 * lectionary year could be three routes, and three thin pages compete with each
 * other for the same query. It is also the wrong shape for the reader: seeing
 * that Year B gets a different antiphon is the useful part, and a per-year route
 * hides exactly that. Years are labelled inline instead.
 *
 * NO NOTATION AND NO TEXT BEYOND THE INCIPIT (the licence forbids derivatives,
 * and plan D7 forbids lyrics on hymn pages for the same instinct). What this page
 * carries is the metadata — which chant, which mode, which psalm, which page —
 * and a link that opens the book where it is printed.
 */
import { Link, useParams } from 'react-router-dom'
import { Breadcrumbs } from '@gjl/studio-chrome'

import { index } from '@/data/propers'
import { qualifierLabel, type ProperChant } from '@/lib/propers'
import { SectionTitle } from '@/components/HymnList'
import { NotFound } from '@/components/NotFound'
import { PageHead } from '@/components/PageHead'
import { SourceNote } from './PropersIndexPage'

const BASE = '/hymnal/propers'

const TYPE_LABEL: Record<string, string> = {
  introit: 'Introit',
  gradual: 'Gradual',
  alleluia: 'Alleluia',
  tract: 'Tract',
  offertory: 'Offertory',
  communion: 'Communion',
  antiphon: 'Antiphon',
}

export default function PropersDayPage() {
  const { slug } = useParams<{ slug: string }>()
  const day = slug ? index.bySlug.get(slug) : undefined

  if (!day) {
    return (
      <NotFound
        title="No such day"
        detail={`Neither book sets propers for "${slug ?? '(none)'}".`}
        backTo={BASE}
        backLabel="Browse propers by day"
      />
    )
  }

  const at = index.days.findIndex((d) => d.slug === day.slug)
  const prev = at > 0 ? index.days[at - 1] : undefined
  const next = at > -1 && at < index.days.length - 1 ? index.days[at + 1] : undefined

  const description =
    `The ${day.types.map((t) => TYPE_LABEL[t]?.toLowerCase() ?? t).join(', ')} for ${day.title} ` +
    `from ${day.books.join(' and ')}, with mode, psalm and the page each is printed on.`

  return (
    <article className="mx-auto max-w-3xl px-5 py-12">
      <PageHead
        title={`${day.title} — minor propers`}
        description={description}
        path={`${BASE}/${day.slug}`}
        schema={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: `${day.title} — minor propers`,
          isPartOf: { '@type': 'WebSite', name: 'Fugue Lab' },
          mainEntity: {
            '@type': 'ItemList',
            numberOfItems: day.chants.length,
            itemListElement: day.chants.map((c, i) => ({
              '@type': 'ListItem',
              position: i + 1,
              name: [TYPE_LABEL[c.type] ?? c.type, c.incipit].filter(Boolean).join(': '),
            })),
          },
        }}
      />
      <Breadcrumbs
        className="mb-6"
        items={[
          { name: 'Propers', url: BASE },
          { name: day.title, url: `${BASE}/${day.slug}` },
        ]}
      />

      <header className="mb-8">
        <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">{day.group}</p>
        <h1 className="text-3xl sm:text-4xl font-display text-foreground mb-3">{day.title}</h1>
        <p className="text-muted-foreground max-w-2xl">
          {day.chants.length} {day.chants.length === 1 ? 'chant' : 'chants'} from {day.books.join(' and ')}.
          {day.yearVaries ? ' Some are appointed for particular lectionary years, marked below.' : ''}
        </p>
      </header>

      <section>
        <SectionTitle>The propers</SectionTitle>
        <ul className="divide-y divide-border border-y border-border">
          {day.chants.map((c, i) => (
            <ChantRow key={`${c.type}-${c.pdfPage}-${i}`} chant={c} />
          ))}
        </ul>
      </section>

      {(prev || next) && (
        <nav className="mt-12 flex justify-between gap-4 border-t border-border pt-5 text-sm" aria-label="Days">
          {prev ? (
            <Link
              to={`${BASE}/${prev.slug}`}
              className="text-muted-foreground hover:text-foreground min-h-11 flex items-center"
            >
              ← {prev.title}
            </Link>
          ) : (
            <span />
          )}
          {next ? (
            <Link
              to={`${BASE}/${next.slug}`}
              className="text-muted-foreground hover:text-foreground min-h-11 flex items-center text-right"
            >
              {next.title} →
            </Link>
          ) : (
            <span />
          )}
        </nav>
      )}

      <SourceNote />
    </article>
  )
}

function ChantRow({ chant }: { chant: ProperChant }) {
  const qualifier = qualifierLabel(chant)
  const rite = chant.source.rite === 'roman' ? 'Roman Rite' : chant.source.rite === 'episcopal' ? 'Episcopal' : null

  // The link text is whatever names the chant in its own book: the American
  // Gradual names chants by Latin incipit, the Gradual Psalms by the psalm.
  // Showing the citation twice would be the alternative, so it is dropped from
  // the detail line when it is already the label.
  const label = chant.incipit ?? chant.citation ?? 'Open the chant'
  const detail = [
    chant.mode ? `Mode ${chant.mode}` : null,
    chant.citation === label ? null : chant.citation,
    rite,
    chant.source.book,
  ].filter(Boolean)

  return (
    <li className="py-3">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <span className="text-xs uppercase tracking-wider text-muted-foreground w-24 shrink-0">
          {TYPE_LABEL[chant.type] ?? chant.type}
        </span>
        <a
          href={chant.url}
          className="text-foreground underline decoration-border underline-offset-4 hover:decoration-foreground"
          rel="noopener noreferrer"
        >
          {label}
        </a>
        {qualifier && (
          <span className="rounded border border-border px-1.5 py-0.5 text-xs text-muted-foreground">{qualifier}</span>
        )}
      </div>
      <p className="mt-1 pl-0 sm:pl-26 text-xs text-muted-foreground">{detail.join(' · ')}</p>
    </li>
  )
}
